// The wording: plain, or the opt-in casual voice.
//
// THE VOICE CHANGES WORDS. Never behaviour, never a number, never what a control
// does. Everything below is one table of sentences and one function that picks a
// column out of it; nothing here branches, writes, or is read by anything that
// is not text on a screen.
//
// PLAIN IS THE DEFAULT AND STAYS THE DEFAULT. General-audience defaults are the
// standing rule (AGENTS.md), so an account that has chosen nothing - which is
// every account - reads the plain column, and the tests assert the plain wording
// by name rather than asserting that some wording appeared.
//
// WHAT MAY NOT BE IN THIS TABLE, which is what keeps it safe:
//
//   1. MICROCOPY ONLY. Encouragement, greetings, empty screens, the sentence the
//      day says back to you. Never a button, a tab, a field label, a setting, a
//      date, or a count. If a person must read it to operate the app, it is not
//      in here and it never changes.
//   2. NOTHING A SCREEN READER IS GIVEN. No `aria-label`, no `role="status"`, no
//      `role="alert"`. A joke in a screen-reader label is a usability defect,
//      not personality, and this product has live `role="alert"` lines precisely
//      so failures are announced.
//   3. NO FACT MOVES. Where a sentence carries a count, a date, a limit or a
//      refusal, the fact is identical in both columns and only the framing
//      moves. The cap notice, the database's own refusal sentences and anything
//      quoting a figure are facts, and none of them is in here at all.
//   4. NOTHING THAT MOCKS. No term is used about a person's own work in a way
//      that belittles it - which is also why refusals and errors are absent:
//      somebody who just lost work or cannot sign in is not in the mood.
//
// REGISTER OVER VOCABULARY, which is why the table is short and why it does not
// need maintaining. The casual voice is mostly lowercase, short, contracted and
// understated; tone does not expire, words do. Four slang tokens are used at all
// - "locked in", "lowkey", "highkey", "ate" - each confined to one line, each
// chosen against dated evidence, and the evidence and the terms deliberately
// rejected are recorded in `docs/design/prototype-v2.md` §2.2 and §2.3 rather
// than copied here.
//
// THERE IS NO REFRESH MECHANISM AND THAT IS DELIBERATE (captain, 2026-08-18:
// "You don't have to worry about the slang changing over time. That's something
// that I will work on if time comes for it"). The prototype carried a
// `VOICE.reviewed` date and showed "Wording last reviewed August 2026" in
// Settings; both are dropped here. A visible review date is a staleness signal
// that implies an owner and a cadence, and the owner took that problem off this
// workstream's plate. The words are the words. Changing them is editing this
// table and nothing else, whenever somebody decides to.

import { useSyncExternalStore } from 'react';
import { readChoice, writePreference } from './preference.ts';

/** The two, and the only values the setting takes. */
export type Voice = 'plain' | 'casual';

/** Off by default, opt-in. This is the whole of that decision. */
export const DEFAULT_VOICE: Voice = 'plain';

/**
 * The segmented control in Settings.
 *
 * ITS OWN LABELS ARE PLAIN IN BOTH VOICES, as is the row that holds it and the
 * fine print under it - a setting you cannot read is a trap, and the one control
 * that turns this off has to be legible to somebody who turned it on by
 * accident.
 */
export const VOICES: readonly { value: Voice; label: string }[] = [
  { value: 'plain', label: 'Plain' },
  { value: 'casual', label: 'Casual' },
];

/**
 * Where the choice is kept, beside `pn_theme` and `pn_mode`.
 *
 * NOT IN THE PRE-PAINT SCRIPT, and the theme's two keys are. The theme has to be
 * resolved before the first paint because a page painted in the wrong mode
 * flashes; the wording is text inside the app, which does not exist until React
 * has mounted and read this. Adding it to `index.html` would be a second copy of
 * a value with nothing to gain from being early.
 */
export const VOICE_KEY = 'pn_voice';

export function isVoice(value: unknown): value is Voice {
  return VOICES.some((option) => option.value === value);
}

// ------------------------------------------------------------------ the pack -

/**
 * A key names a SENTENCE, not a place - `rem.allDone` is the same sentence
 * wherever the reminders run out, and two screens drawing it read from one
 * entry. That is what stops the two voices being kept in step by hand.
 */
export type VoiceKey = keyof typeof LINES;

/**
 * The pairs approved in `docs/design/prototype-v2.md` §2, plus the lines this
 * client needs that the prototype did not.
 *
 * BOTH VOICES SIT ON ONE LINE so a reviewer sees the default and the alternative
 * together and can diff them by eye. `plain` is what everyone reads; `casual` is
 * the opt-in.
 *
 * THE CLIENT-ONLY LINES COVER GRAMMAR OR A NEW GREETING SURFACE. `day.oneLeft`
 * exists because the client fixed a plural the prototype never hit ("1 things
 * left"), and leaving it plain would have put a plain sentence under the bar
 * and a casual one in the reminders card on the same screen - the "setting that
 * appears broken" §2.6 describes. The two Today greeting lines keep its returning
 * greeting distinct from first run; each has a named and anonymous branch so a
 * blank profile cannot leave punctuation behind. `firstrun.titleAnon` covers the
 * same missing-name case on the original welcome screen. All use only the
 * understated register already approved above.
 *
 * `carry.head` is the one entry with no caller: carry-forward is designed
 * (§1.5) and not built in this client. It is kept rather than dropped so that
 * whoever builds it finds the approved pair instead of inventing a second one.
 */
const LINES = {
  'day.allDone': {
    plain: 'That is the whole day.',
    casual: 'that’s the whole day. you ate.',
  },
  'day.someLeft': {
    plain: '{n} left across {m} schedule{s}.',
    casual: '{n} to go. lock in.',
  },
  'day.oneLeft': {
    plain: 'One thing left.',
    casual: 'one to go. lock in.',
  },
  'day.greeting': {
    plain: 'It’s good to see you, {name}. Take things at your own pace.',
    casual: 'hey {name}, it’s good to see you. take things at your own pace.',
  },
  'day.greetingAnon': {
    plain: 'It’s good to see you. Take things at your own pace.',
    casual: 'hey, it’s good to see you. take things at your own pace.',
  },
  'carry.head': {
    plain: '{n} unfinished from the last few days',
    casual: '{n} didn’t get done. no stress.',
  },
  // The casual column names TODAY, and the panel it heads is drawn for whichever
  // day the chooser is on - so it is true only when that day IS the account's
  // today. `emptyDayTitle` in `day/day-model.ts` is where that is decided, and it
  // falls back to the plain heading on every other day rather than voicing a
  // wrong date over a body that names the right one. Deliberately NOT a
  // fourteenth pair: the approved pack is thirteen, a day-neutral casual heading
  // is copy the captain has not written, and the reasoning is at that function.
  'empty.day.title': {
    plain: 'Nothing scheduled.',
    casual: 'nothing on today.',
  },
  // The subject is the weekday, and it is the same day in both columns - only
  // its capitalisation moves, which is register and not a fact. `emptyDayBody`
  // in `day/day-model.ts` is where that happens, at the one call site.
  'empty.day.body': {
    plain:
      '{subject} is clear. Perfect Note only shows the schedules that have something due, so a quiet day looks quiet.',
    casual:
      '{subject}’s clear. perfect note only shows what’s actually due, so a quiet day looks quiet.',
  },
  'empty.notes.title': {
    plain: 'No notes match.',
    casual: 'nothing matches.',
  },
  'firstrun.title': {
    plain: 'Welcome, {name}.',
    casual: 'hey {name}.',
  },
  'firstrun.titleAnon': {
    plain: 'Welcome.',
    casual: 'hey.',
  },
  // Both figures are the server's and both are identical in the two columns.
  // "on {days}" becomes "across {days}", which moves no number.
  'year.headline': {
    plain: 'You finished {things} in {year}, on {days}.',
    casual: 'you finished {things} in {year}, across {days}. lowkey a lot.',
  },
  'year.nostreaks': {
    plain: 'No streaks here. A missed day is a pale square, not a broken chain.',
    casual: 'no streaks here. miss a day and it’s a pale square. nothing breaks.',
  },
  'rem.allDone': {
    plain: 'Nothing left on this list.',
    casual: 'list’s clear. locked in.',
  },
  'rem.empty': {
    plain: 'Nothing to remember right now.',
    casual: 'nothing to remember right now.',
  },
  'rem.emptyBody': {
    plain:
      'A reminder is one thing you need to do once. It waits here until you do it, and then it is finished for good.',
    casual:
      'a reminder is one thing you need to do once. it waits here until you do it, then it’s done for good.',
  },
  'rem.oneLeft': {
    plain: 'One thing left.',
    casual: 'one left. highkey doable.',
  },
} as const satisfies Record<string, { plain: string; casual?: string }>;

/** The pack, for anything that has to read the table rather than a line of it. */
export const VOICE_LINES: Readonly<Record<VoiceKey, { plain: string; casual?: string }>> = LINES;

/**
 * One sentence, in the voice asked for.
 *
 * NOT CALLED `say`, which is the prototype's name for it: `say` is already this
 * client's transient line at the foot of the screen, passed as a prop into
 * Settings, Reminders, Notes and the board. Two different functions under one
 * identifier, one of them shadowing the other inside a component, is a bug that
 * type-checks - the screen would toast a voice key instead of drawing a
 * sentence. The setting is called Wording, so this is.
 *
 * PURE, AND THE VOICE IS AN ARGUMENT rather than something this module reaches
 * for. It is the same shape as `hueIn(hex, mode)` in `edit/edit-model.ts` and
 * for the same reason: the models that call it are documented as pure, and a
 * function that reads a mutable module variable is neither pure nor testable
 * without setting one first. `useVoice()` below is what supplies the argument in
 * a component.
 *
 * A MISSING `casual` FALLS BACK TO `plain`, so a half-finished revision of the
 * table degrades to the default voice rather than breaking a screen.
 */
export function wording(
  key: VoiceKey,
  voice: Voice,
  vars?: Readonly<Record<string, string | number>>,
): string {
  const entry = LINES[key];
  const template: string = (voice === 'casual' && entry.casual) || entry.plain;
  if (vars === undefined) return template;
  return Object.entries(vars).reduce(
    (line, [name, value]) => line.split(`{${name}}`).join(String(value)),
    template,
  );
}

// ------------------------------------------------------- the chosen wording --

/**
 * WHAT THIS PAGE WAS TOLD TO USE, kept beside the two mirrors rather than
 * instead of them.
 *
 * A browser can refuse both `localStorage` and the cookie - private browsing
 * with cookies off, a full quota - and every write in `preference.ts` is
 * swallowed on purpose, because a wording preference may not cost a blank page.
 * Without this, the only record of a choice made in such a browser is the text
 * already on screen, and the next component to mount would read the DEFAULT for
 * somebody who has just pressed Casual and is looking at it.
 *
 * Failing to write a choice down is a reason it will not survive a RELOAD. It is
 * not a reason to overrule it while the page is still up. The theme reached the
 * same answer for the same reason (`theme.ts`, `chosen`).
 */
let chosen: Voice | null = null;

/**
 * The wording in force.
 *
 * The order the three answers are tried in is `readChoice` in `preference.ts`,
 * which owns it for every per-browser preference; this file supplies only its
 * own vocabulary and its own default.
 */
export function currentVoice(): Voice {
  return readChoice(VOICE_KEY, isVoice, chosen, DEFAULT_VOICE);
}

export function storeVoice(voice: Voice): void {
  chosen = voice;
  writePreference(VOICE_KEY, voice);
  for (const tell of listeners) tell();
}

const listeners = new Set<() => void>();

function subscribeVoice(tell: () => void): () => void {
  listeners.add(tell);
  return () => {
    listeners.delete(tell);
  };
}

/**
 * The wording, for a component that draws a sentence.
 *
 * `useSyncExternalStore` rather than a context and a provider, which is the same
 * choice `useMode()` makes and for the same reason: a provider would put the
 * wording in `App.tsx`, and every screen that says anything would then depend on
 * being rendered inside it - including the sheets, which several screens open.
 * The server snapshot is the same function because there is no server: the
 * client is static files (§4.3), so there is no render that has no browser.
 */
export function useVoice(): Voice {
  return useSyncExternalStore(subscribeVoice, currentVoice, currentVoice);
}
